import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Check, Church, CalendarClock, Trash2, Image as ImageIcon, MapPin, BookmarkPlus, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { localeOf } from "./storyEditorUtils";
import { Switch } from "@/components/ui/switch";
import { imageVariant } from "@/lib/mediaUrl";

// Feuille glissante depuis le bas, dans le cadre de l'éditeur (sombre par défaut, claire pour la musique).
import { maxDatetimeLocal } from "@/lib/scheduleLimits";
export const BottomSheet = ({ open, onClose, title, children, light = false, testId, maxH = "75dvh" }) => (
  <AnimatePresence>
    {open && (
      <>
        <motion.div key="bd" className="absolute inset-0 z-40 bg-black/50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, pointerEvents: "none", transition: { duration: 0.18 } }} onClick={onClose} data-testid={testId ? `${testId}-backdrop` : undefined} />
        <motion.div
          key="sheet"
          className="absolute inset-x-0 bottom-0 z-50 rounded-t-3xl border-t overflow-hidden flex flex-col"
          style={{ maxHeight: maxH, background: light ? "var(--yalola-surface)" : "rgba(15,23,42,0.94)", borderColor: light ? "var(--yalola-border)" : "rgba(255,255,255,0.12)", color: light ? "var(--yalola-text)" : "#fff", paddingBottom: "calc(var(--sa-bottom, 0px) + 8px)", backdropFilter: "blur(20px)" }}
          initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%", pointerEvents: "none", transition: { duration: 0.2, ease: "easeIn" } }} transition={{ type: "spring", damping: 28, stiffness: 320 }}
          data-testid={testId}
        >
          <div className="mx-auto mt-2 h-1 w-10 rounded-full shrink-0" style={{ background: light ? "var(--yalola-border)" : "rgba(255,255,255,.3)" }} />
          {title && <div className="px-4 pt-2 pb-1 text-sm font-semibold shrink-0">{title}</div>}
          <div className="overflow-y-auto px-4 pb-3 flex-1 min-h-0">{children}</div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

const row = "w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-left text-sm font-semibold bg-white/5 active:bg-white/15 transition-colors";

// Recherche de membres (@mention) — partagée par la feuille Stickers et la feuille Partager.
export const MentionSearch = ({ onPick, exclude = [], t, autoFocus = false }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setRes([]); return undefined; }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: s } });
        setRes((data.users || []).filter((u) => !exclude.includes(u.id)).slice(0, 6));
      } catch { setRes([]); }
    }, 300);
    return () => clearTimeout(id);
  }, [q, exclude]);
  return (
    <div>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
        <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus={autoFocus} placeholder={t("story.mention_search", "Rechercher un membre…")} className="w-full rounded-full border pl-9 pr-3 py-2 text-sm outline-none bg-white/10 border-white/15 text-white placeholder:text-white/50" data-testid="story-fs-mention-input" />
      </div>
      {q.trim().length > 0 && q.trim().length < 2 && <div className="text-[11px] mt-1.5 opacity-60">{t("story.mention_search_hint", "Tape au moins 2 lettres")}</div>}
      {res.length > 0 && (
        <div className="mt-2 space-y-1" data-testid="story-fs-mention-results">
          {res.map((u) => (
            <button key={u.id} type="button" onClick={() => { onPick(u); setQ(""); setRes([]); }} className={row} data-testid={`story-fs-mention-result-${u.id}`}>
              {u.avatar_url ? <img src={imageVariant(u.avatar_url, 48)} alt="" className="h-8 w-8 rounded-full object-cover" /> : <span className="h-8 w-8 rounded-full grid place-items-center text-white text-xs" style={{ background: "var(--yalola-primary)" }}>{u.name?.[0]}</span>}
              <span className="truncate">{u.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const MentionSheet = ({ open, onClose, onPick, exclude, t }) => (
  <BottomSheet open={open} onClose={onClose} title={t("story.sticker_mention", "Mention")} testId="story-fs-mention-sheet" maxH="70dvh">
    <MentionSearch onPick={onPick} exclude={exclude} t={t} autoFocus />
  </BottomSheet>
);

// Sticker « Lieu » : ville du profil pré-remplie + saisie libre (aucune permission).
export const LocationSheet = ({ open, onClose, defaultCity, onPick, t }) => {
  const [city, setCity] = useState(defaultCity || "");
  useEffect(() => { if (open) setCity(defaultCity || ""); }, [open, defaultCity]);
  const submit = () => { const v = city.trim(); if (v) onPick(v); };
  return (
    <BottomSheet open={open} onClose={onClose} title={t("story.sticker_location", "Lieu")} testId="story-fs-location-sheet" maxH="50dvh">
      <div className="flex gap-2 mt-1">
        <div className="relative flex-1">
          <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "#F97316" }} />
          <input value={city} onChange={(e) => setCity(e.target.value.slice(0, 60))} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} autoFocus placeholder={t("story.location_prompt", "Quelle ville ?")} className="w-full rounded-full border pl-9 pr-3 py-2 text-sm outline-none bg-white/10 border-white/15 text-white placeholder:text-white/50" data-testid="story-fs-location-input" />
        </div>
        <button type="button" onClick={submit} disabled={!city.trim()} className="h-10 px-4 rounded-full text-sm font-bold text-white disabled:opacity-40" style={{ background: "var(--yalola-primary)" }} data-testid="story-fs-location-add">{t("common.add", "Ajouter")}</button>
      </div>
    </BottomSheet>
  );
};

// Sticker « Lien » (façon Instagram) : on colle une URL, la carte d'aperçu apparaît sur la story (photo, vidéo ou fond).
const normalizeLinkInput = (raw) => {
  const v = String(raw || "").trim().split(/\s+/)[0] || "";
  if (!v) return null;
  const url = /^https?:\/\//i.test(v) ? v : `https://${v}`;
  try { const u = new URL(url); return /^https?:$/.test(u.protocol) && u.hostname.includes(".") ? u.toString() : null; } catch { return null; }
};
export const LinkSheet = ({ open, onClose, initial = "", onPick, t }) => {
  const [value, setValue] = useState(initial || "");
  const [err, setErr] = useState(false);
  useEffect(() => { if (open) { setValue(initial || ""); setErr(false); } }, [open, initial]);
  const submit = () => {
    const url = normalizeLinkInput(value);
    if (!url) { setErr(true); return; }
    onPick(url);
  };
  return (
    <BottomSheet open={open} onClose={onClose} title={t("story.sticker_link", "Lien")} testId="story-fs-link-sheet" maxH="50dvh">
      <div className="flex gap-2 mt-1">
        <div className="relative flex-1">
          <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "#38BDF8" }} />
          <input type="url" inputMode="url" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={value} onChange={(e) => { setValue(e.target.value.slice(0, 1000)); setErr(false); }} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} autoFocus placeholder={t("story.link_prompt", "Colle un lien (https://…)")} className="w-full rounded-full border pl-9 pr-3 py-2 text-sm outline-none bg-white/10 border-white/15 text-white placeholder:text-white/50" data-testid="story-fs-link-input" />
        </div>
        <button type="button" onClick={submit} disabled={!value.trim()} className="h-10 px-4 rounded-full text-sm font-bold text-white disabled:opacity-40" style={{ background: "var(--yalola-primary)" }} data-testid="story-fs-link-add">{t("common.add", "Ajouter")}</button>
      </div>
      <p className="mt-2 text-[11px]" style={{ color: err ? "#FCA5A5" : "rgba(255,255,255,.6)" }} data-testid="story-fs-link-hint" data-error={err ? 1 : undefined}>
        {err ? t("story.link_invalid", "Ce lien n'est pas valide.") : t("story.link_hint", "La carte se pose sur la story : glisse-la où tu veux, un tap l'ouvrira.")}
      </p>
    </BottomSheet>
  );
};

// Choix de l'église (quand plusieurs sont autorisées) → publie aussitôt.
export const ChurchPickSheet = ({ open, onClose, churches, onPick, t }) => (
  <BottomSheet open={open} onClose={onClose} title={t("story.pick_church", "Choisir l'église")} testId="story-fs-church-sheet">
    <div className="space-y-1.5 mt-1">
      {churches.map((c) => (
        <button key={c.id} type="button" onClick={() => onPick(c.id)} className={row} data-testid={`story-fs-church-${c.id}`}>
          <span className="h-9 w-9 rounded-full grid place-items-center bg-sky-500 text-white shrink-0"><Church size={16} /></span>
          <span className="min-w-0"><span className="block truncate">{c.name}</span>{c.city && <span className="block text-[11px] font-normal opacity-60 truncate">{c.city}</span>}</span>
        </button>
      ))}
    </div>
  </BottomSheet>
);

// Menu ⋯ : programmer, changer / retirer le média, abandonner.
export const MoreSheet = ({ open, onClose, hasMedia, onSchedule, onRemoveMedia, onDiscard, galleryNode, scheduledAt, editMode = false, onSaveTemplate, lang, t }) => (
  <BottomSheet open={open} onClose={onClose} title={t("story.more_title", "Options")} testId="story-fs-more-sheet" maxH="60dvh">
    <div className="space-y-1.5 mt-1">
      <button type="button" onClick={onSchedule} className={row} data-testid="story-fs-more-schedule">
        <CalendarClock size={18} style={{ color: "var(--yalola-primary)" }} />
        <span className="flex-1">{t("story.schedule", "Programmer la publication")}</span>
        {scheduledAt && <span className="text-[11px] font-normal opacity-70">{new Date(scheduledAt).toLocaleString(localeOf(lang), { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>}
      </button>
      {galleryNode}
      {onSaveTemplate && (
        <button type="button" onClick={onSaveTemplate} className={row} data-testid="story-fs-more-save-template">
          <BookmarkPlus size={18} style={{ color: "var(--yalola-primary)" }} /> {t("story.save_as_template", "Enregistrer comme modèle de l'église")}
        </button>
      )}
      {hasMedia && (
        <button type="button" onClick={onRemoveMedia} className={row} data-testid="story-fs-more-remove-media">
          <ImageIcon size={18} className="opacity-80" /> {t("story.remove_media", "Retirer le média")}
        </button>
      )}
      <button type="button" onClick={onDiscard} className={`${row} text-red-300`} data-testid="story-fs-more-discard">
        <Trash2 size={18} /> {editMode ? t("story.discard_changes", "Abandonner les modifications") : t("story.discard", "Abandonner")}
      </button>
    </div>
  </BottomSheet>
);

const minDatetimeLocal = () => {
  const d = new Date(Date.now() + 5 * 60000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// Programmation : interrupteur + date/heure ; la story part automatiquement à l'heure choisie.
export const ScheduleSheet = ({ open, onClose, scheduledAt, setScheduledAt, t }) => {
  const on = !!scheduledAt;
  return (
    <BottomSheet open={open} onClose={onClose} title={t("story.schedule", "Programmer la publication")} testId="story-fs-schedule-sheet" maxH="50dvh">
      <div className="flex items-center justify-between py-2">
        <span className="text-sm">{t("story.scheduled_badge", "Programmée")}</span>
        <Switch checked={on} onCheckedChange={(v) => setScheduledAt(v ? minDatetimeLocal() : "")} data-testid="story-fs-schedule-switch" />
      </div>
      {on && (
        <input type="datetime-local" value={scheduledAt} min={minDatetimeLocal()} max={maxDatetimeLocal()} onChange={(e) => setScheduledAt(e.target.value)} className="mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none bg-white/10 border-white/15 text-white" style={{ colorScheme: "dark" }} data-testid="story-fs-schedule-input" />
      )}
      <button type="button" onClick={onClose} className="mt-4 w-full h-11 rounded-full text-sm font-bold text-white flex items-center justify-center gap-1.5" style={{ background: "var(--yalola-primary)" }} data-testid="story-fs-schedule-done"><Check size={16} /> {t("story.done", "Terminé")}</button>
    </BottomSheet>
  );
};

// Fermeture avec contenu : garder le brouillon, abandonner, ou continuer.
// Mode édition (annonce programmée) : pas de brouillon → abandonner les modifications ou continuer.
export const CloseSheet = ({ open, onClose, onKeep, onDiscard, editMode = false, t }) => (
  <BottomSheet open={open} onClose={onClose} title={t("story.close_title", "Fermer l'éditeur ?")} testId="story-fs-close-sheet" maxH="50dvh">
    <div className="space-y-1.5 mt-1">
      {!editMode && <button type="button" onClick={onKeep} className={row} data-testid="story-fs-close-keep"><Check size={18} style={{ color: "var(--yalola-primary)" }} /> {t("story.keep_draft_close", "Garder le brouillon et fermer")}</button>}
      <button type="button" onClick={onDiscard} className={`${row} text-red-300`} data-testid="story-fs-close-discard"><Trash2 size={18} /> {editMode ? t("story.discard_changes", "Abandonner les modifications") : t("story.discard", "Abandonner")}</button>
      <button type="button" onClick={onClose} className={row} data-testid="story-fs-close-continue"><X size={18} className="opacity-70" /> {t("story.keep_editing", "Continuer")}</button>
    </div>
  </BottomSheet>
);

// « Enregistrer comme modèle de l'église » : église (si plusieurs) + nom → POST /churches/{id}/story-templates.
export const SaveTemplateSheet = ({ open, onClose, churches, defaultChurchId, onSave, busy, t }) => {
  const [churchId, setChurchId] = useState(defaultChurchId || churches[0]?.id || "");
  const [name, setName] = useState("");
  useEffect(() => {
    if (!open) return;
    setChurchId(defaultChurchId || churches[0]?.id || "");
    setName(t("story.tpl_name_default", "Modèle du {date}").replace("{date}", new Date().toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" })));
  }, [open, defaultChurchId, churches, t]);
  const submit = () => { const v = name.trim(); if (v && churchId) onSave({ churchId, name: v }); };
  return (
    <BottomSheet open={open} onClose={onClose} title={t("story.save_as_template", "Enregistrer comme modèle de l'église")} testId="story-fs-save-template-sheet" maxH="60dvh">
      {churches.length > 1 && (
        <select value={churchId} onChange={(e) => setChurchId(e.target.value)} className="w-full rounded-xl bg-white/10 border border-white/15 text-white text-sm px-3 py-2 outline-none mb-2" style={{ colorScheme: "dark" }} data-testid="story-fs-save-template-church">
          {churches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
      <input value={name} onChange={(e) => setName(e.target.value.slice(0, 60))} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} placeholder={t("story.tpl_name", "Nom du modèle")} className="w-full rounded-full border px-4 py-2.5 text-sm outline-none bg-white/10 border-white/15 text-white placeholder:text-white/50" data-testid="story-fs-save-template-name" />
      <button type="button" onClick={submit} disabled={busy || !name.trim() || !churchId} className="mt-3 w-full h-11 rounded-full text-sm font-bold text-white flex items-center justify-center gap-1.5 disabled:opacity-50" style={{ background: "var(--yalola-primary)" }} data-testid="story-fs-save-template-submit">
        <BookmarkPlus size={16} /> {busy ? t("story.publishing", "Publication…") : t("story.save", "Enregistrer")}
      </button>
    </BottomSheet>
  );
};
